import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TownCalcCard } from "@/components/calc/TownCalcCard";
import { CompareTable } from "@/components/calc/CompareTable";
import { SignupBox } from "@/components/calc/SignupBox";
import { MoversBanner } from "@/components/calc/MoversBanner";

const Pill = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-4 py-2 text-sm border transition-colors ${on ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] text-[#3B1B48] hover:border-[#3B1B48]"}`}>{children}</button>
);

export default function CalculatorsPage() {
  const [towns, setTowns] = useState(null);
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [picked, setPicked] = useState([]);
  useEffect(() => { document.title = "Free relocation calculators · The Expat Studio"; api.get("/public/calculators").then((r) => setTowns(r.data)).catch(() => setTowns([])); }, []);
  const list = useMemo(() => towns || [], [towns]);
  const countriesL = useMemo(() => [...new Set(list.map((t) => t.country))].sort(), [list]);
  const regions = useMemo(() => [...new Set(list.filter((t) => !country || t.country === country).map((t) => t.region).filter(Boolean))].sort(), [list, country]);
  const shown = list.filter((t) => (!country || t.country === country) && (!region || t.region === region));
  const toggle = (slug) => {
    if (!picked.includes(slug) && picked.length >= 4) { toast.message("You can compare up to 4 towns. Remove one to add another."); return; }
    setPicked((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));
  };
  const view = () => document.getElementById("calc-compare")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const compared = picked.map((s) => list.find((t) => t.slug === s)).filter(Boolean);
  const [tableVisible, setTableVisible] = useState(false);
  useEffect(() => {
    const el = document.getElementById("calc-compare");
    if (!el) return undefined;
    const ob = new IntersectionObserver(([e]) => setTableVisible(e.isIntersecting && compared.length > 1), { threshold: 0.15 });
    ob.observe(el);
    return () => ob.disconnect();
  }, [compared.length]);

  return (
    <div className="min-h-screen paper-grain" data-testid="calculators-page">
      <header className="border-b border-[#E2D9CC]"><div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <a href="https://theexpatstudio.com" className="font-display text-xl text-[#3B1B48]" data-testid="calc-brand-link">The Expat Studio</a>
        <a href="https://theexpatstudio.com" className="text-sm text-[#8C827A] hover:text-[#3B1B48]" data-testid="calc-shop-link">Visit the shop</a>
      </div></header>
      <main className="relative max-w-7xl mx-auto px-6 pt-14 pb-32 space-y-12">
        <div className="rise max-w-3xl">
          <p className="eyebrow mb-3">Free tools · Small Towns series</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#3B1B48] leading-[1.05]">Compare the real cost of moving to a small European town</h1>
          <p className="text-base text-[#6B5B4E] mt-5">Property, tax and pet-moving calculators for every town we cover. Tick up to four towns to see them side by side.</p>
        </div>
        <MoversBanner />
        <div className="space-y-3" data-testid="calc-filters">
          <div className="flex flex-wrap gap-2"><Pill on={!country} onClick={() => { setCountry(""); setRegion(""); }} testId="calc-country-all">All countries</Pill>
            {countriesL.map((c) => <Pill key={c} on={country === c} onClick={() => { setCountry(c); setRegion(""); }} testId={`calc-country-${c.toLowerCase()}`}>{c}</Pill>)}</div>
          {regions.length > 1 && <div className="flex flex-wrap gap-2"><Pill on={!region} onClick={() => setRegion("")} testId="calc-region-all">All regions</Pill>
            {regions.map((r) => <Pill key={r} on={region === r} onClick={() => setRegion(r)} testId={`calc-region-${r.toLowerCase().replace(/\W+/g, "-")}`}>{r}</Pill>)}</div>}
        </div>
        <div id="calc-compare" className="scroll-mt-6"><CompareTable towns={compared} onRemove={toggle} /></div>
        {compared.length === 1 && <p className="text-sm text-[#8C827A]" data-testid="calc-compare-hint">Pick one more town to compare.</p>}
        {towns === null ? <p className="eyebrow" data-testid="calc-loading">Loading towns…</p> : shown.length === 0 ? <p data-testid="calc-empty" className="text-[#8C827A]">No towns here yet.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="calc-town-grid">
            {shown.map((t, i) => <TownCalcCard key={t.slug} t={t} i={i} picked={picked.includes(t.slug)} onPick={() => toggle(t.slug)} />)}
          </div>)}
        <SignupBox towns={compared} all={list} />
      </main>
      {compared.length > 0 && !tableVisible && (
        <div data-testid="calc-compare-tray" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4 bg-[#1C181D]/90 backdrop-blur-md text-[#FAF7F2] pl-5 pr-2 py-2 shadow-xl rise">
          <span className="text-sm">{compared.map((t) => t.town).join(" · ")}</span>
          {compared.length > 1 ? <button onClick={view} data-testid="calc-compare-view" className="bg-[#C59B27] text-[#1C181D] text-sm px-4 py-2 hover:bg-[#d8ae3a] transition-colors">View comparison</button>
            : <span className="text-xs text-[#FAF7F2]/60 pr-3">pick one more</span>}
        </div>)}
      <footer className="border-t border-[#E2D9CC] pt-8 pb-24 text-center text-xs text-[#8C827A]">© The Expat Studio, part of AntelliHQ · AI-assisted research, human-curated judgement</footer>
    </div>
  );
}
